import { useEffect, useState } from 'react';
import { useModel } from '../context/ModelContext';
import { DATASETS, MODEL_TARGETS } from '../utils/modelTargets';
import VariableVisualization from './VariableVisualization';
import ResultsDownload from './ResultsDownload';
import OptionToggle from './shared/OptionToggle';

export default function BaselinePanel() {
  const { baselines, ensureBaseline, loadModel, dataset: activeDataset, framework } = useModel();
  const [dataset, setDataset] = useState(activeDataset || DATASETS[0].id);
  const [view, setView] = useState(framework || MODEL_TARGETS[0].framework);
  const available = MODEL_TARGETS.filter(t => t.dataset === dataset);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    MODEL_TARGETS.filter(t => t.framework === view && t.dataset === dataset).forEach(t => {
      void ensureBaseline(t.framework, t.dataset).catch(() => {
        if (active) setErrors(previous => ({ ...previous, [`${t.framework}/${t.dataset}`]: 'Could not load this baseline.' }));
      });
    });
    return () => { active = false; };
  }, [view, dataset, ensureBaseline, retry]);
  return <div className="baseline-workspace">
    <div className="baseline-toolbar">
    <OptionToggle label="Baseline dataset" value={dataset}
      options={DATASETS.map(d => ({ value: d.id, label: d.label }))} onChange={next => {
        const target = MODEL_TARGETS.find(t => t.dataset === next && t.framework === view) || MODEL_TARGETS.find(t => t.dataset === next)!;
        setDataset(next); setView(target.framework); void loadModel(target.framework, next);
      }} />
    <OptionToggle label="Baseline models" value={view}
      options={available.map(t => ({ value: t.framework, label: t.label }))} onChange={next => {
        setView(next); void loadModel(next, dataset);
      }} />
    </div>
    {available.length === 1 && <p className="comparison-note">This dataset is available for CP2015.</p>}
    <div className="baseline-grid">
      {available.filter(t => t.framework === view).map(t => {
        const key = `${t.framework}/${t.dataset}`;
        const model = baselines[key];
        return <section className="card baseline-card" key={key} aria-label={`${t.label} baseline`}>
          <div className="baseline-card-header">
            <h3>{t.label}</h3>
            {model && <ResultsDownload framework={t.framework} dataset={t.dataset} mode="baseline" onRebuild={async () => { await ensureBaseline(t.framework, t.dataset, true); }} />}
          </div>
          {model?.data_metadata?.monetary_units && <p className="comparison-note">
            Monetary values: {model.data_metadata.monetary_units}. Solved equilibrium baseline.
          </p>}
          {model ? <><VariableVisualization model={model} mode="single" /></> :
            errors[key] ? <p role="alert">{errors[key]} <button onClick={() => {
              setErrors({}); setRetry(x => x + 1);
            }}>Retry</button></p> : <p role="status">Loading baseline…</p>}
        </section>;
      })}
    </div>
  </div>;
}
